import React, { createContext, useContext, useState, useEffect } from 'react';

const EmpresaAuthContext = createContext();

export const useEmpresaAuth = () => {
  const context = useContext(EmpresaAuthContext);
  if (!context) {
    throw new Error('useEmpresaAuth debe ser usado dentro de EmpresaAuthProvider');
  }
  return context;
};

// Lee la sesión guardada de forma síncrona: si se hidratara en un useEffect, el
// primer render vería `empresa === null` y las páginas protegidas mandarían a
// /empresa/login al cargar la URL directamente (p. ej. /empresa/dashboard).
const readStoredEmpresa = () => {
  try {
    const token = localStorage.getItem('empresa-token');
    const empresaData = localStorage.getItem('empresa');
    if (token && empresaData) {
      return JSON.parse(empresaData);
    }
  } catch (error) {
    console.error('Error parsing empresa data:', error);
    try {
      localStorage.removeItem('empresa-token');
      localStorage.removeItem('empresa');
    } catch (e) {
      // sin localStorage no hay sesión que limpiar
    }
  }
  return null;
};

export const EmpresaAuthProvider = ({ children }) => {
  const [empresa, setEmpresa] = useState(readStoredEmpresa);
  // La sesión ya está hidratada en el primer render; se conserva `loading`
  // para quien lo lea.
  const loading = false;

  // Refresca el perfil guardado al cargar la sesión: las sesiones iniciadas
  // antes de una actualización del backend no traen campos nuevos (`slug`,
  // `verified`, logo) y antes se pedía volver a iniciar sesión. Desde la fase 3
  // GET /api/empresa/settings incluye `slug` y `verified` en `businessInfo`.
  // `profileSync`: 'idle' (sin sesión) | 'syncing' | 'done' | 'error'.
  const hasSession = Boolean(empresa);
  const [profileSync, setProfileSync] = useState(hasSession ? 'syncing' : 'idle');

  useEffect(() => {
    if (!hasSession) {
      setProfileSync('idle');
      return undefined;
    }
    const token = localStorage.getItem('empresa-token');
    if (!token) return undefined;

    let cancelled = false;
    setProfileSync('syncing');
    (async () => {
      try {
        const response = await fetch('/api/empresa/settings', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => null);
        if (cancelled) return;
        if (!response.ok || !data || !data.success || !data.businessInfo) {
          setProfileSync('error');
          return;
        }

        const info = data.businessInfo;
        const fresh = {};
        if (info.logoUrl !== undefined) fresh.logoUrl = info.logoUrl;
        if (info.slug) fresh.slug = info.slug;
        if (typeof info.verified === 'boolean') fresh.verified = info.verified;
        if (info.businessName) fresh.businessName = info.businessName;

        setEmpresa((current) => {
          if (!current) return current;
          const changed = Object.keys(fresh).some((k) => current[k] !== fresh[k]);
          if (!changed) return current;
          const merged = { ...current, ...fresh };
          try {
            localStorage.setItem('empresa', JSON.stringify(merged));
          } catch (e) {
            // la sesión sigue en memoria
          }
          return merged;
        });
        setProfileSync('done');
      } catch (error) {
        // sin conexión: se usa lo guardado
        if (!cancelled) setProfileSync('error');
      }
    })();
    return () => { cancelled = true; };
  }, [hasSession]);

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/empresa/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();

      if (data.success) {
        localStorage.setItem('empresa-token', data.token);
        localStorage.setItem('empresa', JSON.stringify(data.empresa));
        setEmpresa(data.empresa);
        return { success: true, empresa: data.empresa };
      }

      return { success: false, error: data.error || 'Error en login' };
    } catch (error) {
      return { success: false, error: 'Error de conexión' };
    }
  };

  const register = async (empresaData) => {
    try {
      // El backend (/api/auth/empresa/register) espera multipart/form-data
      // (request.form + request.files) porque acepta documentos adjuntos.
      // Aceptamos tanto un FormData ya armado (recomendado si hay archivos)
      // como un objeto plano, que convertimos automáticamente.
      const body = empresaData instanceof FormData
        ? empresaData
        : Object.keys(empresaData).reduce((formData, key) => {
            const value = empresaData[key];
            if (value !== undefined && value !== null) {
              formData.append(key, value);
            }
            return formData;
          }, new FormData());

      const response = await fetch('/api/auth/empresa/register', {
        method: 'POST',
        body
      });
      const data = await response.json();

      if (data.success) {
        localStorage.setItem('empresa-token', data.token);
        localStorage.setItem('empresa', JSON.stringify(data.empresa));
        setEmpresa(data.empresa);
        return { success: true, empresa: data.empresa };
      }

      return { success: false, error: data.error || 'Error en registro' };
    } catch (error) {
      return { success: false, error: 'Error de conexión' };
    }
  };

  const logout = () => {
    setEmpresa(null);
    localStorage.removeItem('empresa-token');
    localStorage.removeItem('empresa');
  };

  const value = {
    empresa,
    login,
    register,
    logout,
    loading,
    profileSync,
    setEmpresa
  };

  return (
    <EmpresaAuthContext.Provider value={value}>
      {children}
    </EmpresaAuthContext.Provider>
  );
};
